import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { ConfirmDialog } from '../ConfirmDialog';
import { LoadError, errorMessage } from '../LoadError';
import { StatusBadge, inr } from './PurchaseBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DETAIL_GRID = 'min-w-[1040px] grid-cols-[150px_1fr_50px_95px_105px_50px_85px_105px_140px_90px]';
const round2 = n => Math.round((n + Number.EPSILON) * 100) / 100;
const withGst = line => {
  const gstValue = line.gst_amount ?? round2((line.amount || 0) * (line.gst_pct || 0) / 100);
  return { ...line, gstValue, totalValue: round2((line.amount || 0) + gstValue) };
};

const ReverseBox = ({ busy, invoiceNo, lineCount, totalQty, onReverse, onCancel }) => {
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const run = async () => { if (!(await onReverse(reason.trim()))) throw new Error('reverse failed'); };
  return (
    <div className="space-y-2 rounded-xl border border-red-100 bg-red-50/40 p-3" data-testid="purchase-reverse-box">
      <p className="text-[12px] text-red-700">Reversing takes the stock back out and restores the previous cost. It can then be re-entered correctly.</p>
      <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (required, at least 3 characters)" aria-label="Reason for reversing" className="text-[13px]" data-testid="purchase-reverse-reason" />
      <div className="flex flex-wrap gap-2">
        <Button variant="destructive" size="sm" disabled={busy || reason.trim().length < 3} onClick={() => setConfirmOpen(true)} data-testid="purchase-reverse-confirm">Reverse invoice</Button>
        <Button variant="outline" size="sm" disabled={busy} onClick={onCancel} data-testid="purchase-reverse-cancel">Cancel</Button>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Reverse invoice ${invoiceNo}?`}
        description="This takes the stock back out of the warehouse and restores each SKU's previous cost. The invoice stays on record as Reversed."
        confirmLabel="Reverse invoice"
        busyLabel="Reversing…"
        destructive
        onConfirm={run}
        testId="purchase-reverse-confirm-dialog"
      >
        <div className="rounded-lg bg-gray-50 p-3 text-[13px]" data-testid="purchase-reverse-confirm-summary">
          <p>{lineCount} {lineCount === 1 ? 'line' : 'lines'} · <b>{Number(totalQty || 0).toLocaleString('en-IN')}</b> units removed from stock</p>
          <p className="mt-1 text-gray-600">Reason: “{reason.trim()}”</p>
        </div>
      </ConfirmDialog>
    </div>
  );
};

export const PurchaseDetailDialog = ({ purchaseId, onOpenChange, country, isAdmin, canEdit = true, onChanged, onEdit }) => {
  const [invoice, setInvoice] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [showReverse, setShowReverse] = useState(false);
  const [confirmAction, setConfirmAction] = useState('post'); // 'delete' | 'post' (kept while closing so the text doesn't flip)
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    setInvoice(null);
    setLoadError(null);
    setShowReverse(false);
    setConfirmOpen(false);
    if (!purchaseId) return undefined;
    const controller = new AbortController();
    axios.get(`${API}/purchases/${purchaseId}`, { params: { country }, signal: controller.signal }).then(res => setInvoice(res.data))
      .catch(error => { if (!axios.isCancel(error)) setLoadError(error); });
    return () => controller.abort();
  }, [purchaseId, country, reloadKey]);

  const act = async (request, message) => {
    setBusy(true);
    try {
      const res = await request();
      toast.success(message);
      if (res.data?.id) setInvoice(res.data); else onOpenChange(false);
      onChanged();
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail && typeof detail === 'object' && !Array.isArray(detail) && detail.message ? detail.message : errorMessage(error, 'Action failed'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const lines = (invoice?.lines || []).map(withGst);
  const invoiceAmount = round2(lines.reduce((sum, l) => sum + (l.amount || 0), 0));
  const invoiceGst = round2(lines.reduce((sum, l) => sum + l.gstValue, 0));
  const totalQty = invoice?.totals?.qty ?? lines.reduce((sum, l) => sum + (Number(l.qty) || 0), 0);
  const runConfirmed = async () => {
    const ok = confirmAction === 'delete'
      ? await act(() => axios.delete(`${API}/purchases/${invoice.id}`, { params: { country } }), 'Draft deleted')
      : await act(() => axios.post(`${API}/purchases/${invoice.id}/post`, null, { params: { country } }), 'Invoice posted: stock and cost updated');
    if (!ok) throw new Error('action failed');
  };
  return (
    <Dialog open={Boolean(purchaseId)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-[1120px] overflow-y-auto rounded-2xl" data-testid="purchase-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3 text-[17px] font-semibold">
            Invoice {invoice?.invoice_no} {invoice && <StatusBadge status={invoice.status} />}
          </DialogTitle>
          <DialogDescription>{invoice ? `${invoice.supplier} · ${invoice.invoice_date}` : (loadError ? 'This invoice could not be loaded.' : 'Loading…')}</DialogDescription>
        </DialogHeader>
        {loadError && !invoice && (
          <>
            <LoadError what="this invoice" error={loadError} onRetry={() => setReloadKey(k => k + 1)} testId="purchase-detail-load-error" />
            <div className="flex justify-end border-t pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="purchase-detail-close">Close</Button>
            </div>
          </>
        )}
        {invoice && (
          <>
            <div className="overflow-x-auto rounded-xl border">
              <div className={`grid ${DETAIL_GRID} gap-2 bg-gray-50 px-3 py-2 text-[11px] font-semibold uppercase text-gray-500`}>
                <span>SKU</span><span>Product</span><span className="text-right">Qty</span><span className="text-right">Unit cost</span><span className="text-right">Amount excl. GST</span><span className="text-right">GST %</span><span className="text-right">GST</span><span className="text-right">Total incl. GST</span><span>Stock added to</span><span className="text-right">Prev. cost</span>
              </div>
              {lines.map((line, i) => (
                <div key={`${line.sku_code}-${i}`} className={`grid ${DETAIL_GRID} gap-2 border-t px-3 py-2 text-[12px]`} data-testid={`purchase-detail-line-${i}`}>
                  <span className="font-mono">{line.sku_code}</span><span className="truncate text-gray-600">{line.product_name}</span>
                  <span className="text-right">{line.qty}</span>
                  <span className="text-right font-mono">{line.unit_cost != null ? inr(line.unit_cost) : '–'}</span>
                  <span className="text-right">{inr(line.amount)}</span><span className="text-right">{line.gst_pct}</span>
                  <span className="text-right">{inr(line.gstValue)}</span><span className="text-right font-semibold" data-testid={`purchase-detail-line-total-${i}`}>{inr(line.totalValue)}</span>
                  <span className="truncate font-mono text-[11px]">{line.stock_qty ? `+${line.stock_qty} ${line.stock_sku}` : '–'}</span>
                  <span className="text-right text-gray-500">{line.prev_cost != null ? inr(line.prev_cost) : '–'}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-[13px]" data-testid="purchase-detail-totals">
              <span>Qty <b>{invoice.totals?.qty}</b></span><span>Amount excl. GST <b>{inr(invoiceAmount)}</b></span><span>GST <b>{inr(invoiceGst)}</b></span>
              {Number(invoice.charges) > 0 && <span>Freight &amp; charges <b data-testid="purchase-detail-charges">{inr(Number(invoice.charges))}</b> (in landed cost)</span>}
              <span>Invoice total incl. GST <b data-testid="purchase-detail-total-incl-gst">{inr(invoiceAmount + invoiceGst + (Number(invoice.charges) || 0))}</b></span>
            </div>
            <div className="space-y-1 text-[12px] text-gray-500" data-testid="purchase-detail-history">
              {(invoice.history || []).map((h, i) => <p key={`${h.at}-${i}`}>{h.action} · {new Date(h.at).toLocaleString('en-IN')} · {h.by}{h.reason ? ` · “${h.reason}”` : ''}</p>)}
            </div>
            {showReverse && invoice.status === 'POSTED' && (
              <ReverseBox busy={busy} invoiceNo={invoice.invoice_no} lineCount={lines.length} totalQty={totalQty} onCancel={() => setShowReverse(false)}
                onReverse={reason => act(() => axios.post(`${API}/purchases/${invoice.id}/reverse`, { reason }, { params: { country } }), 'Invoice reversed: stock and cost restored')} />
            )}
            <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
              {invoice.status === 'DRAFT' && !isAdmin && canEdit && <p className="mr-auto self-center text-[12px] text-gray-600" data-testid="purchase-detail-admin-hint">Only an admin can post this draft.</p>}
              {invoice.status === 'DRAFT' && canEdit && (
                <>
                  <Button variant="outline" disabled={busy} onClick={() => { setConfirmAction('delete'); setConfirmOpen(true); }} data-testid="purchase-delete-draft">Delete draft</Button>
                  <Button variant="outline" onClick={() => onEdit(invoice)} data-testid="purchase-edit-draft">Edit draft</Button>
                  {isAdmin && <Button disabled={busy} onClick={() => { setConfirmAction('post'); setConfirmOpen(true); }} className="bg-[#007AFF] text-white" data-testid="purchase-detail-post">Post invoice</Button>}
                </>
              )}
              {invoice.status === 'POSTED' && isAdmin && !showReverse && <Button variant="outline" className="text-red-700" onClick={() => setShowReverse(true)} data-testid="purchase-reverse">Reverse…</Button>}
            </div>
            <ConfirmDialog
              open={confirmOpen}
              onOpenChange={setConfirmOpen}
              title={confirmAction === 'delete' ? `Delete draft invoice ${invoice.invoice_no}?` : `Post invoice ${invoice.invoice_no}?`}
              description={confirmAction === 'delete'
                ? 'The draft and its lines will be removed. This cannot be undone.'
                : 'Posting adds these units to warehouse stock and sets the latest purchase cost for each SKU. To undo it later you will need to reverse the invoice.'}
              confirmLabel={confirmAction === 'delete' ? 'Delete draft' : 'Post invoice'}
              busyLabel={confirmAction === 'delete' ? 'Deleting…' : 'Posting…'}
              destructive={confirmAction === 'delete'}
              onConfirm={runConfirmed}
              testId={confirmAction === 'delete' ? 'purchase-delete-confirm-dialog' : 'purchase-post-confirm-dialog'}
            >
              {confirmAction === 'post' && (
                <div className="rounded-lg bg-gray-50 p-3 text-[13px]" data-testid="purchase-post-confirm-summary">
                  <p>{lines.length} {lines.length === 1 ? 'line' : 'lines'} · <b>{Number(totalQty || 0).toLocaleString('en-IN')}</b> units added to stock</p>
                  <p className="mt-1">Value excl. GST <b>{inr(invoiceAmount)}</b> · incl. GST <b>{inr(invoiceAmount + invoiceGst)}</b></p>
                </div>
              )}
            </ConfirmDialog>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
